import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { LOGO, NAV_LINKS, TOPBAR, CONTACT } from "@/data/content";
import { scrollToId } from "@/components/SmoothScroll";
import Magnetic from "@/components/Magnetic";

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const onHome = location.pathname === "/";

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const go = (id) => {
        setOpen(false);
        if (onHome) scrollToId(id);
        else navigate(`/#${id}`);
    };

    return (
        <>
            <div className="fixed top-0 left-0 right-0 z-[70]">
                <div className="french-stripe h-[3px] w-full" />
                <div className="bg-navy text-white text-center py-1.5 hidden sm:block">
                    <span className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-medium font-body">
                        {TOPBAR}
                    </span>
                </div>
            </div>

            <header
                className={
                    "fixed top-[3px] sm:top-[34px] left-0 right-0 z-[65] transition-all duration-500 " +
                    (scrolled ? "glass py-2" : "bg-transparent py-4")
                }
                data-testid="navbar"
            >
                <nav className="max-w-7xl mx-auto px-5 md:px-10 flex items-center justify-between">
                    <Link to="/" onClick={() => onHome && scrollToId("accueil")} className="flex items-center gap-3" data-testid="nav-logo">
                        <img src={LOGO} alt="IGN Services Alsace" className="w-12 h-12 object-contain block" />
                        <span className="leading-tight">
                            <span className="block text-white font-display font-semibold text-[15px]">IGN Services</span>
                            <span className="block text-electric-soft text-[10px] tracking-[0.3em] uppercase font-body">Alsace</span>
                        </span>
                    </Link>

                    <div className="hidden lg:flex items-center gap-8">
                        {NAV_LINKS.map((l) => (
                            <button
                                key={l.id}
                                onClick={() => go(l.id)}
                                className="text-[13px] text-white/80 hover:text-white transition-colors font-body relative group"
                                data-testid={`nav-link-${l.id}`}
                            >
                                {l.label}
                                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-electric group-hover:w-full transition-all duration-300" />
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-3">
                        <a href={`tel:${CONTACT.phone}`} className="hidden md:flex items-center gap-2 text-white/80 hover:text-white text-[13px] font-body" data-testid="nav-phone">
                            <Phone className="w-4 h-4" />
                            {CONTACT.phoneDisplay}
                        </a>
                        <Magnetic strength={0.3}>
                            <button
                                onClick={() => go("contact")}
                                className="hidden sm:inline-flex bg-electric text-white px-5 py-2.5 text-[12px] uppercase tracking-wide font-semibold font-body hover:bg-white hover:text-navy transition-colors duration-300"
                                data-testid="nav-cta-devis"
                            >
                                Demander un devis
                            </button>
                        </Magnetic>
                        <button
                            className="lg:hidden text-white p-2"
                            onClick={() => setOpen((v) => !v)}
                            aria-label="Menu"
                            data-testid="nav-mobile-toggle"
                        >
                            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </nav>
            </header>

            {/* Mobile menu */}
            <div
                className={
                    "fixed inset-0 z-[64] bg-ink/97 backdrop-blur-md transition-all duration-400 lg:hidden " +
                    (open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none")
                }
                data-testid="mobile-menu"
            >
                <div className="flex flex-col items-center justify-center h-full gap-7">
                    {NAV_LINKS.map((l) => (
                        <button
                            key={l.id}
                            onClick={() => go(l.id)}
                            className="text-white text-2xl font-display font-light"
                            data-testid={`mobile-link-${l.id}`}
                        >
                            {l.label}
                        </button>
                    ))}
                    <button
                        onClick={() => go("contact")}
                        className="mt-4 bg-electric text-white px-8 py-4 text-sm uppercase tracking-wide font-semibold font-body"
                        data-testid="mobile-cta-devis"
                    >
                        Demander un devis
                    </button>
                    <a href={`tel:${CONTACT.phone}`} className="text-electric-soft font-body">{CONTACT.phoneDisplay}</a>
                </div>
            </div>
        </>
    );
}
